// Ally root component. Composes useAlly + open/close state + sub-parts.
// All content is injected via props — UI has no `@content/` imports.

import { useState } from 'react';
import { AllyTrigger } from './AllyTrigger';
import { AllyPanel, type AllyPanelCopy } from './AllyPanel';
import { useAlly } from '@lib/hooks/useAlly';

export interface AllyCopy extends AllyPanelCopy {
  /** Visible label on the closed trigger pill. */
  triggerLabel: string;
}

export interface AllyProps {
  /** CAG bundle sent as system context to /api/ally. */
  context: string;
  /** First assistant message shown when the panel opens. */
  greeting: string;
  /** Suggested starter questions when the chat is empty. */
  suggestions: ReadonlyArray<string>;
  /** All visible UI strings. */
  copy: AllyCopy;
  /** Override the default `/api/ally` endpoint (for testing). */
  endpoint?: string;
}

export function Ally({ context, greeting, suggestions, copy, endpoint }: AllyProps) {
  const [open, setOpen] = useState(false);
  const { messages, busy, send } = useAlly({
    context,
    greeting,
    ...(endpoint ? { endpoint } : {}),
  });

  return (
    <div className="ally">
      {open ? (
        <AllyPanel
          messages={messages}
          busy={busy}
          suggestions={suggestions}
          onSend={send}
          onClose={() => setOpen(false)}
          copy={copy}
        />
      ) : (
        <AllyTrigger onOpen={() => setOpen(true)} label={copy.triggerLabel} />
      )}
    </div>
  );
}
